import { useState, useEffect, useRef } from "react";
import React from "react";
import Icon from "../components/Icon";
import UnsavedChangesGuard from "../components/UnsavedChangesGuard";
import ConfirmDelete from "../components/ConfirmDelete";
import { cascadeForward, addWorkdays } from "../utils";

// Local copy — also used by Jobs/Timesheets still in CRM.jsx
const JOB_COLORS = ["#b91c1c","#1d4ed8","#059669","#d97706","#7c3aed","#0891b2","#be185d","#65a30d"];
function jobColor(idx) { return JOB_COLORS[idx % JOB_COLORS.length]; }

// ─── SCHEDULE ─────────────────────────────────────────────────────────────────
const NOTE_COLORS = ["#b91c1c","#1d4ed8","#059669","#d97706","#7c3aed","#0891b2","#be185d","#65a30d","#c2410c","#0369a1"];

function noteColor(c) { return NOTE_COLORS.includes(c) ? c : NOTE_COLORS[0]; }

// Returns all dates in a week starting from monday
function getWeekStart(d) {
  const day = new Date(d);
  const diff = day.getDay()===0 ? -6 : 1-day.getDay();
  day.setDate(day.getDate()+diff);
  day.setHours(0,0,0,0);
  return day;
}

function isoDate(d) { return d.toISOString().split("T")[0]; }

function entryOnDay(entry, dayStr) {
  if (!entry.startDate || !entry.endDate) return false;
  return dayStr >= entry.startDate && dayStr <= entry.endDate;
}

function addDays(isoStr, n) {
  const d = new Date(isoStr+"T12:00:00");
  d.setDate(d.getDate()+n);
  return d.toISOString().split("T")[0];
}
function dayDiff(isoA, isoB) {
  return Math.round((new Date(isoA+"T12:00:00")-new Date(isoB+"T12:00:00"))/86400000);
}
function skipWeekend(dateStr) {
  const d = new Date(dateStr+'T12:00:00');
  if (d.getDay()===6) d.setDate(d.getDate()+2);
  else if (d.getDay()===0) d.setDate(d.getDate()+1);
  return d.toISOString().split('T')[0];
}
function countWorkdays(startStr, endStr) {
  if (!startStr || !endStr) return 1;
  const start = new Date(startStr+'T12:00:00');
  const end   = new Date(endStr+'T12:00:00');
  let count = 0;
  const d = new Date(start);
  while (d <= end) { if (d.getDay()!==0&&d.getDay()!==6) count++; d.setDate(d.getDate()+1); }
  return Math.max(1, count);
}

function NoteForm({ t, note, customers, crews, jobs, onSave, onClose, onDelete }) {
  const today = new Date().toISOString().split("T")[0];
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [form, setForm] = useState(() => {
    const base = note || {
      title:"", clientName:"", customerId:"", startDate:today, endDate:today,
      color:NOTE_COLORS[0], crewId:"", crewName:"", notes:"",
    };
    // Resolve crewId from crewName for legacy notes that predate crewId field
    if (base.crewName && !base.crewId) {
      const matched = (crews||[]).find(c=>c.name===base.crewName);
      if (matched) return {...base, crewId: matched.id};
    }
    return {...base, crewId: base.crewId||""};
  });
  const [duration, setDuration] = useState(() =>
    countWorkdays((note||{}).startDate||new Date().toISOString().split("T")[0], (note||{}).endDate||new Date().toISOString().split("T")[0])
  );

  return (
    <div className="modal-backdrop" style={{zIndex:250}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title" style={{display:"flex",alignItems:"center",gap:8}}>
            📌 {form.id ? "Modifier note" : "Nouvelle note de planification"}
          </div>
          <button className="btn-icon" title={t?.common?.close||"Fermer"} onClick={onClose}><Icon name="x"/></button>
        </div>
        <div className="modal-body">
          {form.replacedByJobId&&(()=>{const rj=(jobs||[]).find(j=>j.id===form.replacedByJobId);return(<div style={{marginBottom:14,padding:"8px 12px",background:"var(--green-bg)",borderRadius:"var(--radius-sm)",fontSize:12,color:"var(--green)",border:"1px solid #bbf7d0"}}>✅ Remplacée par : <strong>{rj?.title||form.replacedByJobId}</strong></div>);})()}
          <div className="form-grid form-grid-2">
            <div className="form-group" style={{gridColumn:"1/-1"}}>
              <label>Description *</label>
              <input className="form-input" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="Ex: Patio Murphy, Excavation Tremblay..."/>
            </div>
            <div className="form-group">
              <label>Client (de la liste)</label>
              <select className="form-select" value={form.customerId||""} onChange={e=>{
                const c = customers.find(x=>x.id===e.target.value);
                setForm(p=>({...p,customerId:e.target.value,clientName:c?`${c.firstName} ${c.lastName}`:p.clientName}));
              }}>
                <option value="">— Sélectionner —</option>
                {customers.map(c=><option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Ou nom libre</label>
              <input className="form-input" value={form.clientName||""} onChange={e=>setForm(p=>({...p,clientName:e.target.value}))} placeholder="Nom du client..."/>
            </div>
            <div className="form-group">
              <label>Date début *</label>
              <input className="form-input" type="date" value={form.startDate} onChange={e=>{
                const newEnd=addWorkdays(e.target.value,duration-1);
                setForm(p=>({...p,startDate:e.target.value,endDate:newEnd}));
              }}/>
            </div>
            <div className="form-group">
              <label>Durée / Duration</label>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <input className="form-input" type="number" min={1} value={duration} onFocus={e=>e.target.select()} style={{width:70}} onChange={e=>{
                  const d=Math.max(1,parseInt(e.target.value)||1);
                  setDuration(d);
                  setForm(p=>({...p,endDate:addWorkdays(p.startDate,d-1)}));
                }}/>
                <span style={{fontSize:12,color:"var(--text-3)",whiteSpace:"nowrap"}}>jour(s) / day(s)</span>
              </div>
            </div>
            <div className="form-group">
              <label>Équipe</label>
              <select className="form-select" value={form.crewId||""} onChange={e=>{
                const crew=(crews||[]).find(c=>c.id===e.target.value);
                setForm(p=>({...p,crewId:e.target.value||null,crewName:crew?.name||""}));
              }}>
                <option value="">— Aucune équipe —</option>
                {(crews||[]).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Couleur</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:4}}>
                {NOTE_COLORS.map(c=>(
                  <div key={c} onClick={()=>setForm(p=>({...p,color:c}))} style={{
                    width:24,height:24,borderRadius:"50%",background:c,cursor:"pointer",
                    border:form.color===c?"3px solid var(--text)":"3px solid transparent",
                  }}/>
                ))}
              </div>
            </div>
            <div className="form-group" style={{gridColumn:"1/-1"}}>
              <label>Notes</label>
              <textarea className="form-textarea" value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Détails supplémentaires..."/>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{display:"flex",alignItems:"center"}}>
          {form.id&&onDelete&&(
            <button className="btn btn-danger" onClick={()=>setConfirmingDelete(true)}>
              <Icon name="trash" size={13}/>Supprimer
            </button>
          )}
          <div style={{flex:1}}/>
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" disabled={!form.title||!form.startDate||!form.endDate} onClick={()=>onSave(form)}>
            {form.id?"Sauvegarder":"Créer note"}
          </button>
        </div>
      </div>
      {confirmingDelete&&(
        <ConfirmDelete
          message={`Supprimer la note "${form.title}"?`}
          onConfirm={()=>{ setConfirmingDelete(false); onDelete(form.id); }}
          onCancel={()=>setConfirmingDelete(false)}
        />
      )}
    </div>
  );
}


function Schedule({ t, jobs, customers, teamMembers, crews, scheduleNotes, addScheduleNote, updateScheduleNote, deleteScheduleNote, updateJob, role = "admin", onOpenJob }) {
  const today = new Date();
  const [refDate, setRefDate] = useState(new Date());
  const [timeView, setTimeView] = useState("week"); // "week" | "month" | "season"
  const [crewView, setCrewView] = useState("job");  // "job" | "crew"
  const [noteForm, setNoteForm] = useState(null); // null | {} | existing note
  const [selectedJobItem, setSelectedJobItem] = useState(null);
  const [jobEditForm, setJobEditForm] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [dropConfirm, setDropConfirm] = useState(null);
  const [seasonZoom, setSeasonZoom] = useState(1);
  const [isDirty, setIsDirty] = useState(false);
  const [guard, setGuard] = useState(null);
  const dragState = useRef(null);
  const [dragGhost, setDragGhost] = useState(null);
  useEffect(() => { setIsDirty(false); setGuard(null); }, [selectedJobItem]);
  const tryClose = (closeFn, saveFn) => { if (!isDirty) { closeFn(); return; } setGuard({ close: closeFn, save: saveFn }); };
  const saveJobDetail = async () => {
    const original = selectedJobItem;
    const updatedFields = { ...jobEditForm };
    if (updatedFields.startDate && original.startDate && updatedFields.startDate !== original.startDate) {
      const diffDays = Math.round((new Date(updatedFields.startDate) - new Date(original.startDate)) / (1000*60*60*24));
      if (diffDays !== 0) {
        const shiftDate = (dateStr) => { if (!dateStr) return dateStr; return skipWeekend(addDays(dateStr, diffDays)); };
        updatedFields.endDate = shiftDate(original.endDate);
        updatedFields.phases = (original.phases||[]).map(ph=>({...ph, startDate:shiftDate(ph.startDate), endDate:shiftDate(ph.endDate)}));
      }
    }
    await updateJob(original.id, {...original, ...updatedFields});
    const updatedJob = {...original, ...updatedFields};
    const cascadeChanges = cascadeForward(updatedJob, jobs, scheduleNotes||[]);
    if(cascadeChanges.length > 0){
      await Promise.all(cascadeChanges.map(change => {
        if(change.type==='job') return updateJob(change.id, change.updates);
        if(change.type==='note') return updateScheduleNote(change.id, change.updates);
        return Promise.resolve();
      }));
      const getConflictingFixed = () => {
        const crewId = updatedJob.crewId;
        if(!crewId) return [];
        return (jobs||[]).filter(j=>{
          if(!j.fixedDate||j.id===updatedJob.id||j.crewId!==crewId) return false;
          const jStart=j.startDate, jEnd=j.endDate||j.startDate;
          const tStart=updatedJob.startDate, tEnd=updatedJob.endDate||updatedJob.startDate;
          return jStart&&jEnd&&tStart&&tEnd&&jStart<=tEnd&&jEnd>=tStart;
        });
      };
      const conflictingFixed = getConflictingFixed();
      if(conflictingFixed.length>0){
        const names=conflictingFixed.map(j=>j.name||j.id).join(', ');
        alert(`Attention : les travaux suivants ont une date fixe et chevauchent l'équipe assignée — ils n'ont pas été déplacés :\n${names}`);
      }
    }
    setSelectedJobItem(null);
    setJobEditForm(null);
  };

  const dayNames = Object.values(t.schedule.days);
  const scheduledJobs = jobs.filter(j=>j.startDate&&j.endDate);
  const crewColor = (crewId) => {
    if (!crewId) return '#6b7280';
    return (crews||[]).find(c=>c.id===crewId)?.color || '#6b7280';
  };
  // Resolve a note's crew by crewId first, then crewName string fallback (legacy docs)
  const getNoteCrewId = (note) => {
    if (note.crewId) return note.crewId;
    if (note.crewName) {
      const matched = (crews||[]).find(c=>c.name===note.crewName);
      return matched?.id || null;
    }
    return null;
  };
  const getTextColor = (hexColor) => {
    if (!hexColor || !hexColor.startsWith('#')) return '#ffffff';
    const r = parseInt(hexColor.slice(1,3), 16);
    const g = parseInt(hexColor.slice(3,5), 16);
    const b = parseInt(hexColor.slice(5,7), 16);
    const luminance = (0.299*r + 0.587*g + 0.114*b) / 255;
    return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
  };
  const ts = t.schedule;
  const startDrag = (e, type, id, origStart, origEnd, fromDay, label) => {
    dragState.current = { type, id, origStart, origEnd, fromDay, label, startX: e.clientX, startY: e.clientY, isDragging: false };
  };
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragState.current) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;
      if (!dragState.current.isDragging) {
        if (Math.sqrt(dx*dx + dy*dy) < 5) return;
        dragState.current.isDragging = true;
      }
      setDragGhost({ x: e.clientX, y: e.clientY, label: dragState.current.label });
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const dayCell = el?.closest('[data-day]');
      setDragOverDay(dayCell ? dayCell.getAttribute('data-day') : null);
    };
    const onMouseUp = (e) => {
      if (!dragState.current) return;
      if (dragState.current.isDragging) {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const dayCell = el?.closest('[data-day]');
        const toDay = dayCell ? dayCell.getAttribute('data-day') : null;
        if (toDay && toDay !== dragState.current.fromDay) {
          const shift = dayDiff(toDay, dragState.current.fromDay);
          const newStart = skipWeekend(addDays(dragState.current.origStart, shift));
          const workdayDuration = countWorkdays(dragState.current.origStart, dragState.current.origEnd);
          const newEnd = workdayDuration <= 1 ? newStart : addWorkdays(newStart, workdayDuration - 1);
          setDropConfirm({ ...dragState.current, newStart, newEnd });
        }
      }
      dragState.current = null;
      setDragGhost(null);
      setDragOverDay(null);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);
  const confirmDrop = async () => {
    if(!dropConfirm) return;
    if(dropConfirm.type==="job"){
      const job=jobs.find(j=>j.id===dropConfirm.id);
      if(job){
        const offset=dayDiff(dropConfirm.newStart,dropConfirm.origStart);
        const shiftedPhases=(job.phases||[]).map(ph=>({
          ...ph,
          startDate:ph.startDate?skipWeekend(addDays(ph.startDate,offset)):ph.startDate,
          endDate:ph.endDate?skipWeekend(addDays(ph.endDate,offset)):ph.endDate,
        }));
        await updateJob(dropConfirm.id,{...job,startDate:dropConfirm.newStart,endDate:dropConfirm.newEnd,phases:shiftedPhases});
        const updatedJob={...job,startDate:dropConfirm.newStart,endDate:dropConfirm.newEnd,phases:shiftedPhases};
        const cascadeChanges=cascadeForward(updatedJob,jobs,scheduleNotes||[]);
        if(cascadeChanges.length>0){
          await Promise.all(cascadeChanges.map(change=>{
            if(change.type==='job') return updateJob(change.id,change.updates);
            if(change.type==='note') return updateScheduleNote(change.id,change.updates);
            return Promise.resolve();
          }));
          const getConflictingFixed=()=>{
            const crewId=updatedJob.crewId;
            if(!crewId) return [];
            return (jobs||[]).filter(j=>{
              if(!j.fixedDate||j.id===updatedJob.id||j.crewId!==crewId) return false;
              const jStart=j.startDate, jEnd=j.endDate||j.startDate;
              const tStart=updatedJob.startDate, tEnd=updatedJob.endDate||updatedJob.startDate;
              return jStart&&jEnd&&tStart&&tEnd&&jStart<=tEnd&&jEnd>=tStart;
            });
          };
          const conflictingFixed=getConflictingFixed();
          if(conflictingFixed.length>0){
            const names=conflictingFixed.map(j=>j.name||j.id).join(', ');
            alert(`Attention : les travaux suivants ont une date fixe et chevauchent l'équipe assignée — ils n'ont pas été déplacés :\n${names}`);
          }
        }
      }
    } else {
      const note=(scheduleNotes||[]).find(n=>n.id===dropConfirm.id);
      if(note)await updateScheduleNote(dropConfirm.id,{...note,startDate:dropConfirm.newStart,endDate:dropConfirm.newEnd});
    }
    setDropConfirm(null);
  };

  // ── Helpers ──
  const isToday = (d) => d.toDateString()===today.toDateString();

  const getConflictsForDay = (dayStr, sjobs) => {
    const conflictJobIds = new Set();
    sjobs.forEach(j=>{
      if(!j.crewId) return;
      const jOnDay=j.startDate<=dayStr&&j.endDate>=dayStr;
      if(!jOnDay) return;
      sjobs.forEach(other=>{
        if(other.id===j.id||!other.crewId||other.crewId!==j.crewId) return;
        const otherOnDay=other.startDate<=dayStr&&other.endDate>=dayStr;
        if(otherOnDay){conflictJobIds.add(j.id);conflictJobIds.add(other.id);}
      });
    });
    return conflictJobIds;
  };

  // Build rows (job or crew view)
  const buildRows = () => crewView==="job"
    ? scheduledJobs.map(j=>({id:j.id,label:j.title,color:crewColor(j.crewId),jobs:[j],job:j}))
    : (crews||[]).map(crew=>({
        id:crew.id,label:crew.name,color:crew.color,
        jobs:scheduledJobs.filter(j=>j.crewId===crew.id),
        job:null,
      }));

  // ── Week View ──
  const WeekView = () => {
    const weekStart = getWeekStart(refDate);
    const days = Array.from({length:7},(_,i)=>{ const d=new Date(weekStart); d.setDate(d.getDate()+i); return d; });
    const conflictsByDay = new Map(days.map(day=>{ const d=isoDate(day); return [d,getConflictsForDay(d,scheduledJobs)]; }));
    const rows = buildRows();
    const workdays = days.slice(0,5); // Mon–Fri (days[0..4])
    // Returns { si: startColIdx(0-4), cols: spanCount } for a note visible this week, or null
    const noteSpan = (n) => {
      if (!n.startDate||!n.endDate) return null;
      const wStart=isoDate(workdays[0]), wEnd=isoDate(workdays[4]);
      if (n.endDate<wStart||n.startDate>wEnd) return null;
      const visStart=n.startDate<wStart?wStart:n.startDate;
      const visEnd=n.endDate>wEnd?wEnd:n.endDate;
      const si=workdays.findIndex(d=>isoDate(d)===visStart);
      const ei=workdays.findIndex(d=>isoDate(d)===visEnd);
      if (si<0||ei<0) return null;
      return {si, cols:ei-si+1};
    };
    const noteBarStyle = (n, idx, topOffset, spanCols) => ({
      position:"absolute", top:topOffset+idx*22, height:18, left:3,
      width:`calc(${spanCols*100}% + ${spanCols-1}px - 6px)`,
      background:noteColor(n.color)+"30", border:`1.5px dashed ${noteColor(n.color)}`,
      borderRadius:4, padding:"0 5px", fontSize:10, fontWeight:600,
      color:noteColor(n.color), cursor:n.replacedByJobId?"pointer":dragGhost?"grabbing":"grab",
      overflow:"hidden", display:"flex", alignItems:"center", zIndex:2, whiteSpace:"nowrap",
    });

    return (
      <div style={{overflowX:"auto",overflowY:"visible"}}>
        <div style={{display:"grid",gridTemplateColumns:"160px repeat(5,1fr) 40px 40px",minWidth:700,overflow:"visible"}}>
          {/* Header */}
          <div style={{padding:"10px 14px",background:"var(--bg)",borderBottom:"2px solid var(--border)",fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase"}}>{crewView==="job"?"Travail":"Équipe"}</div>
          {days.map((d,i)=>{
            const isWeekend=i>=5;
            return (
              <div key={i} style={{padding:"8px",background:"var(--bg)",borderBottom:"2px solid var(--border)",textAlign:"center",borderLeft:"1px solid var(--border)",opacity:isWeekend?0.6:1}}>
                <div style={{fontSize:isWeekend?9:10,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase"}}>{dayNames[i]}</div>
                {!isWeekend&&<div style={{fontSize:14,fontWeight:700,color:isToday(d)?"var(--red)":"var(--text)",background:isToday(d)?"#fef2f2":"transparent",borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",margin:"2px auto 0"}}>{d.getDate()}</div>}
                {isWeekend&&<div style={{fontSize:10,color:"var(--text-3)",marginTop:2}}>{d.getDate()}</div>}
              </div>
            );
          })}

          {/* Job/crew rows */}
          {rows.map(row=>{
            const rowCrewNotes=crewView==="crew"?(scheduleNotes||[]).filter(n=>getNoteCrewId(n)===row.id):[];
            return (
            <React.Fragment key={row.id}>
              <div style={{padding:"10px 14px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",gap:8,minHeight:48}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:row.color,flexShrink:0}}/>
                <span style={{fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.label}</span>
              </div>
              {days.map((day,di)=>{
                const d=isoDate(day);
                const isWeekend=day.getDay()===0||day.getDay()===6;
                const weekendStyle={borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)",padding:"3px 4px",minHeight:48,background:"#f8f8f8",pointerEvents:"none"};
                if(isWeekend) return <div key={di} style={weekendStyle}/>;
                const rowJobs=row.jobs.filter(j=>j.startDate<=d&&j.endDate>=d);
                const noteBarsHere=rowCrewNotes.filter(n=>{const s=noteSpan(n);return s&&s.si===di;});
                const noteTopBase=3+rowJobs.length*22;
                return (
                  <div key={di}
                    data-day={d}
                    style={{borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)",padding:"3px 4px",minHeight:Math.max(48,(rowJobs.length+noteBarsHere.length)*22+8),display:"flex",flexDirection:"column",gap:2,position:"relative",background:dragOverDay===d?"var(--blue-bg)":"",transition:"background .1s"}}
                  >
                    {rowJobs.map(job=>{
                      const c=crewColor(job.crewId)||row.color;
                      const isConflict=(conflictsByDay.get(d)||new Set()).has(job.id);
                      return <div key={job.id}
                        onMouseDown={role!=="employee"?e=>startDrag(e,"job",job.id,job.startDate,job.endDate,d,job.title):undefined}
                        onClick={()=>{ if(!dragState.current){ setSelectedJobItem(job); setJobEditForm({...job,crewId:job.crewId||"",fixedDate:job.fixedDate||false}); } }}
                        title={`${job.title} · ${job.startDate}→${job.endDate}`}
                        style={{background:isConflict?"#ef4444":c,border:"none",borderRadius:4,padding:"2px 5px",fontSize:10,fontWeight:600,color:isConflict?"#ffffff":getTextColor(c),cursor:dragGhost?"grabbing":"grab",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                      >{isConflict?"⚠ ":""}{job.fixedDate?"📌 ":""}{job.title}</div>;
                    })}
                    {noteBarsHere.map((n,idx)=>{
                      const s=noteSpan(n);
                      return (
                        <div key={n.id}
                          onMouseDown={!n.replacedByJobId&&role!=="employee"?e=>startDrag(e,"note",n.id,n.startDate,n.endDate,d,n.clientName||n.title):undefined}
                          onClick={role!=="employee"?()=>setNoteForm({...n}):undefined}
                          className={n.replacedByJobId?"sched-replaced":""}
                          title={`${n.clientName||n.title} · ${n.startDate}→${n.endDate}`}
                          style={noteBarStyle(n,idx,noteTopBase,s.cols)}
                        >
                          📌 {n.clientName||n.title}
                          {n.replacedByJobId&&<span style={{marginLeft:3,fontSize:8,background:"var(--green-bg)",color:"var(--green)",borderRadius:3,padding:"0 2px"}}>→</span>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
            );
          })}

          {/* Notes row — crew view: unassigned only ("Sans équipe"); job view: all notes ("Notes planif.") */}
          {(()=>{
            const weekendCell = (di) => <div key={di} style={{borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)",padding:"3px 4px",minHeight:48,background:"#f8f8f8",pointerEvents:"none"}}/>;
            const renderNoteBar = (n, idx, bg) => {
              const s=noteSpan(n); if(!s) return null;
              const isUnassigned=!getNoteCrewId(n);
              return (
                <div key={n.id}
                  onMouseDown={!n.replacedByJobId&&role!=="employee"?e=>startDrag(e,"note",n.id,n.startDate,n.endDate,isoDate(workdays[s.si]),n.clientName||n.title):undefined}
                  onClick={role!=="employee"?()=>setNoteForm({...n}):undefined}
                  className={n.replacedByJobId?"sched-replaced":""}
                  style={{...noteBarStyle(n,idx,3,s.cols),opacity:isUnassigned?0.8:1}}
                >
                  {isUnassigned?"⚠ ":"📌 "}{n.clientName||n.title}
                  {n.replacedByJobId&&<span style={{marginLeft:3,fontSize:8,background:"var(--green-bg)",color:"var(--green)",borderRadius:3,padding:"0 2px"}}>→</span>}
                </div>
              );
            };
            if(crewView==="crew"){
              const unassigned=(scheduleNotes||[]).filter(n=>!getNoteCrewId(n));
              const hasInWeek=unassigned.some(n=>noteSpan(n)!==null);
              if(!hasInWeek) return null;
              return (
                <React.Fragment>
                  <div style={{padding:"10px 14px",borderBottom:"1px solid var(--border)",fontSize:12,fontWeight:600,color:"#92400e",display:"flex",alignItems:"center",gap:6,background:"#fffbeb"}}>⚠ Sans équipe</div>
                  {days.map((day,di)=>{
                    const d=isoDate(day);
                    if(day.getDay()===0||day.getDay()===6) return weekendCell(di);
                    const barsHere=unassigned.filter(n=>{const s=noteSpan(n);return s&&s.si===di;});
                    return (
                      <div key={di} data-day={d}
                        style={{borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)",padding:"3px 4px",minHeight:Math.max(48,barsHere.length*22+8),position:"relative",background:dragOverDay===d?"var(--blue-bg)":"#fffbeb",transition:"background .1s"}}
                      >
                        {barsHere.map((n,idx)=>renderNoteBar(n,idx,"#fffbeb"))}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            }
            // job view — all notes as spanning bars
            const allNotes=scheduleNotes||[];
            if(!allNotes.length) return null;
            return (
              <React.Fragment>
                <div style={{padding:"10px 14px",borderBottom:"1px solid var(--border)",fontSize:12,fontWeight:600,color:"var(--text-3)",display:"flex",alignItems:"center",gap:6,background:"#fdfdf9"}}>📌 Notes planif.</div>
                {days.map((day,di)=>{
                  const d=isoDate(day);
                  if(day.getDay()===0||day.getDay()===6) return weekendCell(di);
                  const barsHere=allNotes.filter(n=>{const s=noteSpan(n);return s&&s.si===di;});
                  return (
                    <div key={di} data-day={d}
                      style={{borderBottom:"1px solid var(--border)",borderLeft:"1px solid var(--border)",padding:"3px 4px",minHeight:Math.max(48,barsHere.length*22+8),position:"relative",background:dragOverDay===d?"var(--blue-bg)":"#fdfdf9",transition:"background .1s"}}
                    >
                      {barsHere.map((n,idx)=>renderNoteBar(n,idx,"#fdfdf9"))}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })()}
        </div>
      </div>
    );
  };

  // ── Month View ──
  const MonthView = () => {
    const year=refDate.getFullYear(), month=refDate.getMonth();

    // Build Mon–Sun weeks covering the month + overflow into prev/next month
    const firstOfMonth=new Date(year,month,1);
    const firstDow=(firstOfMonth.getDay()+6)%7; // 0=Mon
    const startMonday=new Date(year,month,1-firstDow);
    const lastOfMonth=new Date(year,month+1,0);
    const lastDow=(lastOfMonth.getDay()+6)%7;
    const daysToSun=lastDow===6?0:7-lastDow-1;
    const endSunday=new Date(lastOfMonth);
    endSunday.setDate(lastOfMonth.getDate()+daysToSun);
    const weeks=[];
    const cur=new Date(startMonday);
    while(cur<=endSunday){
      const wk=[];
      for(let i=0;i<7;i++){wk.push(new Date(cur));cur.setDate(cur.getDate()+1);}
      weeks.push(wk);
    }

    const weekDayLabels=Object.values(t.schedule.days).map(s=>s.toUpperCase());

    // Returns { si: di(0-6 in wk), cols } for a note visible in this week-row, or null
    const noteMonthSpan = (n, wk) => {
      if (!n.startDate||!n.endDate) return null;
      const wkWorkdays = wk.filter(d=>d.getDay()!==0&&d.getDay()!==6);
      if (!wkWorkdays.length) return null;
      const wStart=isoDate(wkWorkdays[0]), wEnd=isoDate(wkWorkdays[wkWorkdays.length-1]);
      if (n.endDate<wStart||n.startDate>wEnd) return null;
      const visStart=n.startDate<wStart?wStart:n.startDate;
      const visEnd=n.endDate>wEnd?wEnd:n.endDate;
      let si=wk.findIndex(d=>isoDate(d)===visStart);
      const ei=wk.findIndex(d=>isoDate(d)===visEnd);
      if (si<0||ei<0) return null;
      // Advance si past any out-of-month days so the bar anchors to the first
      // in-month cell. Out-of-month cells return early without rendering content,
      // so a bar anchored there would never appear.
      while(si<=ei&&wk[si].getMonth()!==month) si++;
      if(si>ei) return null;
      return {si, cols:ei-si+1};
    };
    const activeCrewsForWeek=(wk)=>(crews||[]).filter(crew=>
      scheduledJobs.some(j=>j.crewId===crew.id&&wk.some(d=>{ const ds=isoDate(d); return j.startDate<=ds&&j.endDate>=ds; }))
      ||(scheduleNotes||[]).some(n=>getNoteCrewId(n)===crew.id&&wk.some(d=>entryOnDay(n,isoDate(d))))
    );

    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr) 40px 40px"}}>
          {/* Header */}
          {weekDayLabels.map((d,i)=>{
            const isWeekendHdr=i>=5;
            return <div key={d} style={{background:"var(--bg)",padding:"6px 4px",textAlign:"center",fontSize:isWeekendHdr?9:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",borderBottom:"2px solid var(--border)",borderRight:i<6?"1px solid var(--border)":"none",opacity:isWeekendHdr?0.6:1}}>{d}</div>;
          })}

          {/* Weeks */}
          {weeks.map((wk,wi)=>{
            const activeCrews=activeCrewsForWeek(wk);
            const hasUnassignedForWeek=(scheduleNotes||[]).some(n=>!getNoteCrewId(n)&&noteMonthSpan(n,wk)!==null);
            const renderedRows=[...activeCrews,...(hasUnassignedForWeek?['__notes__']:[])];
            return (
              <React.Fragment key={wi}>
                {renderedRows.map((row,ri)=>{
                  const isNotesRow=row==='__notes__';
                  const isFirstRow=ri===0;
                  const isLastRow=ri===renderedRows.length-1;
                  const rowKey=isNotesRow?'__notes__':row.id;
                  // Precompute notes for this row+week (avoids recomputing per cell)
                  const rowNotes=isNotesRow
                    ?(scheduleNotes||[]).filter(n=>!getNoteCrewId(n))
                    :(scheduleNotes||[]).filter(n=>getNoteCrewId(n)===row.id);
                  // Greedy lane assignment: each bar gets the first lane that doesn't
                  // conflict with any other bar whose column range overlaps its own.
                  // This prevents bars starting on different days from stacking on top
                  // of each other when they visually span the same day columns.
                  const visibleNotesForRow=rowNotes
                    .map(n=>({n,s:noteMonthSpan(n,wk)}))
                    .filter(({s})=>s!==null)
                    .sort((a,b)=>a.s.si-b.s.si);
                  const noteLanes=new Map();
                  for(const {n,s} of visibleNotesForRow){
                    let lane=0;
                    // eslint-disable-next-line no-loop-func
                    while(visibleNotesForRow.some(({n:o,s:os})=>
                      noteLanes.has(o.id)&&noteLanes.get(o.id)===lane&&
                      os.si<=s.si+s.cols-1&&os.si+os.cols-1>=s.si
                    )) lane++;
                    noteLanes.set(n.id,lane);
                  }
                  const rowNoteSlots=visibleNotesForRow.length>0
                    ?Math.max(...visibleNotesForRow.map(({n})=>noteLanes.get(n.id)??0))+1
                    :0;
                  return (
                    <React.Fragment key={rowKey}>
                      {wk.map((day,di)=>{
                        const d=isoDate(day);
                        const inMonth=day.getMonth()===month;
                        const isWeekend=day.getDay()===0||day.getDay()===6;
                        const isLastCol=di===6;
                        const borderRight=!isLastCol?"1px solid var(--border)":"none";
                        const borderBottom=isLastRow?"2px solid var(--border)":"none";
                        const weekendCellStyle={background:"#f8f8f8",minHeight:64,borderRight,borderBottom,pointerEvents:"none"};

                        if(isWeekend){
                          return <div key={`${wi}_${rowKey}_${di}`} style={weekendCellStyle}/>;
                        }

                        if(!inMonth){
                          return (
                            <div key={`${wi}_${rowKey}_${di}`}
                              style={{background:"var(--bg)",minHeight:64,padding:isFirstRow?"2px 3px":"0",borderRight,borderBottom}}
                            >
                              {isFirstRow&&<div style={{fontSize:10,color:"var(--text-3)"}}>{day.getDate()}</div>}
                            </div>
                          );
                        }

                        // Notes spanning bars for this cell (only bars starting on this di)
                        const noteBarsHere=rowNotes.filter(n=>{const s=noteMonthSpan(n,wk);return s&&s.si===di;});

                        if(isNotesRow){
                          return (
                            <div key={`${wi}_notes_${di}`}
                              data-day={d}
                              style={{background:dragOverDay===d?"var(--blue-bg)":"#fffbeb",minHeight:Math.max(64,(isFirstRow?14:0)+rowNoteSlots*15+8),padding:"2px 3px",position:"relative",transition:"background .1s",borderRight,borderBottom}}
                            >
                              {isFirstRow&&<div style={{fontSize:10,fontWeight:isToday(day)?700:400,color:isToday(day)?"var(--red)":"var(--text-3)",marginBottom:2}}>{day.getDate()}</div>}
                              {noteBarsHere.map((n)=>{
                                const s=noteMonthSpan(n,wk);
                                const lane=noteLanes.get(n.id)??0;
                                return (
                                  <div key={n.id}
                                    onMouseDown={!n.replacedByJobId&&role!=="employee"?e=>startDrag(e,"note",n.id,n.startDate,n.endDate,d,n.clientName||n.title):undefined}
                                    onClick={role!=="employee"?()=>setNoteForm({...n}):undefined}
                                    className={n.replacedByJobId?"sched-replaced":""}
                                    style={{position:"absolute",top:(isFirstRow?16:2)+lane*15,height:12,left:2,width:`calc(${s.cols*100}% + ${s.cols-1}px - 4px)`,fontSize:9,fontWeight:600,padding:"0 3px",borderRadius:3,background:noteColor(n.color)+"30",color:noteColor(n.color),border:`1px dashed ${noteColor(n.color)}`,overflow:"hidden",display:"flex",alignItems:"center",whiteSpace:"nowrap",cursor:n.replacedByJobId?"pointer":dragGhost?"grabbing":"grab",zIndex:2,opacity:0.85}}
                                  >⚠ {(n.clientName||n.title).slice(0,16)}{n.replacedByJobId&&<span style={{marginLeft:2,fontSize:7,background:"var(--green-bg)",color:"var(--green)",borderRadius:3,padding:"0 2px"}}>→</span>}</div>
                                );
                              })}
                            </div>
                          );
                        }

                        const crew=row;
                        const c=crew.color||'#6b7280';
                        const dayConflicts=getConflictsForDay(d,scheduledJobs);
                        const uniqueJobs=scheduledJobs.filter(j=>j.crewId===crew.id&&j.startDate<=d&&j.endDate>=d);
                        const bgBase=isToday(day)?dragOverDay===d?"#fce7f3":"#fef2f2":dragOverDay===d?"var(--blue-bg)":"var(--card)";
                        const noteTopBase=(isFirstRow?14:2)+uniqueJobs.length*14;

                        return (
                          <div key={`${wi}_${crew.id}_${di}`}
                            data-day={d}
                            style={{background:bgBase,minHeight:Math.max(64,(isFirstRow?14:0)+uniqueJobs.length*14+rowNoteSlots*15+8),padding:"2px 3px",position:"relative",borderTop:isFirstRow&&isToday(day)?"2px solid var(--red)":"none",borderRight,borderBottom,transition:"background .1s"}}
                          >
                            {isFirstRow&&<div style={{fontSize:10,fontWeight:isToday(day)?700:400,color:isToday(day)?"var(--red)":"var(--text-3)",marginBottom:2}}>{day.getDate()}</div>}
                            {uniqueJobs.map(job=>{
                              const isConflict=dayConflicts.has(job.id);
                              return <div key={job.id}
                                onMouseDown={role!=="employee"?e=>startDrag(e,"job",job.id,job.startDate,job.endDate||job.startDate,d,job.title):undefined}
                                onClick={()=>{ setSelectedJobItem(job); setJobEditForm({...job,crewId:job.crewId||"",fixedDate:job.fixedDate||false}); }}
                                style={{fontSize:9,fontWeight:600,padding:"1px 3px",borderRadius:3,background:isConflict?"#ef4444":c,border:"none",color:isConflict?"#ffffff":getTextColor(c),overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:dragGhost?"grabbing":"grab"}}
                              >{isConflict?"⚠ ":""}{job.fixedDate?"📌 ":""}{job.title.slice(0,14)}</div>;
                            })}
                            {noteBarsHere.map((n)=>{
                              const s=noteMonthSpan(n,wk);
                              const lane=noteLanes.get(n.id)??0;
                              return (
                                <div key={n.id}
                                  onMouseDown={!n.replacedByJobId&&role!=="employee"?e=>startDrag(e,"note",n.id,n.startDate,n.endDate,d,n.clientName||n.title):undefined}
                                  onClick={role!=="employee"?()=>setNoteForm({...n}):undefined}
                                  className={n.replacedByJobId?"sched-replaced":""}
                                  style={{position:"absolute",top:noteTopBase+lane*15,height:12,left:2,width:`calc(${s.cols*100}% + ${s.cols-1}px - 4px)`,fontSize:9,fontWeight:600,padding:"0 3px",borderRadius:3,background:noteColor(n.color)+"30",color:noteColor(n.color),border:`1px dashed ${noteColor(n.color)}`,overflow:"hidden",display:"flex",alignItems:"center",whiteSpace:"nowrap",cursor:n.replacedByJobId?"pointer":dragGhost?"grabbing":"grab",zIndex:2}}
                                >📌 {(n.clientName||n.title).slice(0,14)}{n.replacedByJobId&&<span style={{marginLeft:2,fontSize:7,background:"var(--green-bg)",color:"var(--green)",borderRadius:3,padding:"0 2px"}}>→</span>}</div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Season View ──
  const SeasonView = () => {
    const year=refDate.getFullYear();
    const allEntries=[
      ...scheduledJobs.map(j=>{
        const phaseStart=(j.phases||[]).map(ph=>ph.startDate).filter(Boolean).sort()[0];
        const phaseEnd=(j.phases||[]).map(ph=>ph.endDate).filter(Boolean).sort().reverse()[0];
        return{...j,_type:"job",_color:crewColor(j.crewId),_label:j.title,_jobId:j.id,startDate:j.startDate||phaseStart,endDate:j.endDate||phaseEnd||j.startDate||phaseStart};
      }),
      ...(scheduleNotes||[]).filter(n=>!n.replacedByJobId).map(n=>({...n,_type:"note",_color:noteColor(n.color),_label:n.clientName||n.title})),
    ];
    // Conflict detection
    const seasonConflictJobIds = new Set();
    scheduledJobs.forEach((j,i)=>{
      scheduledJobs.forEach((other,k)=>{
        if(k<=i) return;
        if(!j.crewId||!other.crewId||j.crewId!==other.crewId) return;
        const overlap=j.startDate&&j.endDate&&other.startDate&&other.endDate&&
          j.startDate<=other.endDate&&j.endDate>=other.startDate;
        if(overlap){seasonConflictJobIds.add(j.id);seasonConflictJobIds.add(other.id);}
      });
    });

    // Zoom-aware view bounds
    const ZOOM_MONTHS=[8,4,2];
    const MONTH_NAMES_FR=["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
    const visibleMonths=ZOOM_MONTHS[seasonZoom-1];
    const seasonStart=new Date(year,3,1);
    const seasonEnd=new Date(year,10,30);
    const todayDate=new Date();
    const centerMonth=(todayDate>=seasonStart&&todayDate<=seasonEnd)?todayDate.getMonth():3;
    const halfVisible=Math.floor(visibleMonths/2);
    let viewStartMonth=Math.max(3,centerMonth-halfVisible);
    let viewEndMonth=viewStartMonth+visibleMonths-1;
    if(viewEndMonth>10){viewEndMonth=10;viewStartMonth=viewEndMonth-visibleMonths+1;}
    const viewStart=new Date(year,viewStartMonth,1);
    const viewEnd=new Date(year,viewEndMonth+1,0);
    const totalDays=Math.round((viewEnd-viewStart)/(1000*60*60*24))+1;
    const visibleMonthNames=[];
    for(let m=viewStartMonth;m<=viewEndMonth;m++) visibleMonthNames.push(MONTH_NAMES_FR[m]);

    return (
      <div style={{overflowX:"auto"}}>
        {/* Header with zoom controls */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:14}}>Saison {year}</div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:11,color:"var(--text-3)"}}>{visibleMonthNames[0]} — {visibleMonthNames[visibleMonthNames.length-1]}</span>
            <div style={{display:"flex",gap:4}}>
              <button className="btn btn-secondary btn-sm" onClick={()=>setSeasonZoom(z=>Math.max(1,z-1))} disabled={seasonZoom===1} title="Zoom arrière">−</button>
              <button className="btn btn-secondary btn-sm" onClick={()=>setSeasonZoom(z=>Math.min(3,z+1))} disabled={seasonZoom===3} title="Zoom avant">+</button>
            </div>
          </div>
        </div>

        {/* Month header row */}
        <div style={{display:"grid",gridTemplateColumns:`120px repeat(${visibleMonths},1fr)`,gap:1,background:"var(--border)",marginBottom:6,minWidth:600}}>
          <div style={{background:"var(--bg)",padding:"6px 10px",fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase"}}>Projet</div>
          {visibleMonthNames.map(m=><div key={m} style={{background:"var(--bg)",padding:"6px",textAlign:"center",fontSize:10,fontWeight:700,color:"var(--text-3)"}}>{m}</div>)}
        </div>

        {allEntries.length===0&&<div style={{textAlign:"center",color:"var(--text-3)",padding:"40px",fontSize:13}}>Aucun travail ni note pour cette saison.</div>}

        {allEntries.map((entry,ei)=>{
          const start=new Date((entry.startDate||"")+"T12:00:00");
          const end=new Date((entry.endDate||"")+"T12:00:00");
          if(start>viewEnd||end<viewStart) return null;
          const startOffset=Math.max(0,(start-viewStart)/(1000*60*60*24));
          const endClamped=Math.min(end,viewEnd);
          const duration=Math.max(1,(endClamped-start)/(1000*60*60*24)+1);
          const leftPct=(startOffset/totalDays)*100;
          const widthPct=Math.min((duration/totalDays)*100,100-leftPct);
          const isNote=entry._type==="note";
          const isConflict=!isNote&&seasonConflictJobIds.has(entry._jobId);
          return (
            <div key={ei} style={{display:"grid",gridTemplateColumns:`120px repeat(${visibleMonths},1fr)`,gap:1,marginBottom:2,minHeight:26,minWidth:600,alignItems:"center"}}>
              <div style={{fontSize:11,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"var(--text-2)",padding:"0 4px"}}>{entry._label}</div>
              <div style={{gridColumn:`2/${visibleMonths+2}`,position:"relative",height:22,background:"var(--bg)",borderRadius:4,overflow:"hidden"}}>
                <div style={{
                  position:"absolute",top:2,height:18,
                  left:`${leftPct}%`,width:`${widthPct}%`,
                  background:isNote?entry._color+"30":isConflict?"#ef4444":entry._color,
                  border:isNote?`1.5px dashed ${entry._color}`:"none",
                  borderRadius:4,minWidth:4,cursor:"pointer",
                  display:"flex",alignItems:"center",padding:"0 4px",
                  overflow:"hidden",
                }} onClick={()=>{
                  if(isNote){if(role!=="employee")setNoteForm(entry);}
                  else{const job=scheduledJobs.find(j=>j.id===entry._jobId);if(job){setSelectedJobItem(job);setJobEditForm({...job,crewId:job.crewId||"",fixedDate:job.fixedDate||false});}}
                }}>
                  <span style={{fontSize:9,fontWeight:700,color:isNote?entry._color:isConflict?"#ffffff":getTextColor(entry._color),whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {isNote?"📌 ":""}{isConflict?"⚠ ":""}{entry._label}
                  </span>
                </div>
                {Array.from({length:visibleMonths-1},(_,i)=>(
                  <div key={i} style={{position:"absolute",top:0,bottom:0,left:`${((i+1)/visibleMonths)*100}%`,borderLeft:"1px solid var(--border)",opacity:.4}}/>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Navigation ──
  const navigate = (dir) => {
    const d=new Date(refDate);
    if(timeView==="week") d.setDate(d.getDate()+dir*7);
    else if(timeView==="month") d.setMonth(d.getMonth()+dir);
    else d.setFullYear(d.getFullYear()+dir);
    setRefDate(d);
  };

  const periodLabel = () => {
    if(timeView==="week"){
      const ws=getWeekStart(refDate);
      const we=new Date(ws); we.setDate(we.getDate()+6);
      return `${ws.toLocaleDateString("fr-CA",{month:"short",day:"numeric"})} – ${we.toLocaleDateString("fr-CA",{month:"short",day:"numeric",year:"numeric"})}`;
    }
    if(timeView==="month") return refDate.toLocaleDateString("fr-CA",{month:"long",year:"numeric"});
    return `Saison ${refDate.getFullYear()}`;
  };

  const conflicts = (()=>{
    if(timeView!=="week") return new Set();
    const ws=getWeekStart(refDate);
    const wDays=Array.from({length:7},(_,i)=>{const d=new Date(ws);d.setDate(d.getDate()+i);return d;});
    const all=new Set();
    wDays.forEach(day=>getConflictsForDay(isoDate(day),scheduledJobs).forEach(id=>all.add(id)));
    return all;
  })();

  return (
    <div>
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h1>{t.schedule.title}</h1>
            <p>{scheduledJobs.length} travaux · {(scheduleNotes||[]).filter(n=>!n.replacedByJobId).length} notes de planification</p>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            {/* Time view toggle */}
            <div style={{display:"flex",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)",padding:3,gap:2}}>
              {[["week","Semaine"],["month","Mois"],["season","Saison"]].map(([k,l])=>(
                <button key={k} onClick={()=>setTimeView(k)} style={{padding:"5px 10px",background:timeView===k?"var(--card)":"transparent",border:timeView===k?"1px solid var(--border)":"1px solid transparent",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer",color:timeView===k?"var(--text)":"var(--text-3)",fontFamily:"inherit"}}>{l}</button>
              ))}
            </div>
            {/* Crew/job view toggle (week only) */}
            {timeView==="week"&&(
              <div style={{display:"flex",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)",padding:3,gap:2}}>
                {[["job","Par travail"],["crew","Par équipe"]].map(([k,l])=>(
                  <button key={k} onClick={()=>setCrewView(k)} style={{padding:"5px 10px",background:crewView===k?"var(--card)":"transparent",border:crewView===k?"1px solid var(--border)":"1px solid transparent",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer",color:crewView===k?"var(--text)":"var(--text-3)",fontFamily:"inherit"}}>{l}</button>
                ))}
              </div>
            )}
            {/* Nav */}
            <button className="btn btn-secondary btn-sm" onClick={()=>navigate(-1)}>‹</button>
            <span style={{fontSize:13,fontWeight:500,minWidth:180,textAlign:"center"}}>{periodLabel()}</span>
            <button className="btn btn-secondary btn-sm" onClick={()=>navigate(1)}>›</button>
            <button className="btn btn-secondary btn-sm" onClick={()=>setRefDate(new Date())}>{t.schedule.today}</button>
            {role!=="employee"&&(
              <button className="btn btn-primary btn-sm" onClick={()=>setNoteForm({})}>
                <Icon name="plus" size={12}/>Note
              </button>
            )}
          </div>
        </div>
      </div>

      {dropConfirm&&(
        <div className="modal-backdrop" style={{zIndex:400}} onClick={e=>e.target===e.currentTarget&&setDropConfirm(null)}>
          <div className="modal" style={{maxWidth:360}}>
            <div className="modal-header">
              <div className="modal-title">📅 {ts.moveJob}</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>setDropConfirm(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body" style={{textAlign:"center",padding:"24px 20px"}}>
              <div style={{fontSize:15,fontWeight:600,marginBottom:8}}>{dropConfirm.label}</div>
              <div style={{fontSize:13,color:"var(--text-3)",marginBottom:4}}>{ts.newPeriod}</div>
              <div style={{fontSize:16,fontWeight:700,color:"var(--red)"}}>{dropConfirm.newStart} → {dropConfirm.newEnd}</div>
            </div>
            <div className="modal-footer" style={{justifyContent:"center",gap:12}}>
              <button className="btn btn-secondary" style={{minWidth:100}} onClick={()=>setDropConfirm(null)}>{t.common.cancel}</button>
              <button className="btn btn-primary" style={{minWidth:100}} onClick={confirmDrop}>{ts.confirm}</button>
            </div>
          </div>
        </div>
      )}
      <div className="page-body">
        {conflicts.size>0&&(
          <div style={{marginBottom:12,padding:"10px 16px",background:"#fef9c3",border:"1px solid #fde047",borderRadius:8,fontSize:12,color:"#854d0e",display:"flex",alignItems:"center",gap:8}}>
            <span>⚠️</span><span><strong>Conflit d'horaire</strong> — plusieurs travaux assignés à la même équipe le même jour.</span>
          </div>
        )}

        <div className="card" style={{padding:"16px 20px"}}>
          {timeView==="week"&&<WeekView/>}
          {timeView==="month"&&<MonthView/>}
          {timeView==="season"&&<SeasonView/>}
        </div>

        {/* Legend */}
        <div style={{display:"flex",gap:16,marginTop:10,fontSize:11,color:"var(--text-3)",alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:14,height:10,background:"var(--red)",borderRadius:2}}/> Travail confirmé</div>
          <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:14,height:10,background:"#1d4ed830",border:"1.5px dashed #1d4ed8",borderRadius:2}}/> Note de planification</div>
          <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:14,height:10,background:"#fee2e2",border:"1px solid #ef4444",borderRadius:2}}/> Conflit d'équipe</div>
        </div>

      </div>

      {/* Job detail modal */}
      {selectedJobItem&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&tryClose(()=>{setSelectedJobItem(null);setJobEditForm(null);},saveJobDetail)}>
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-header">
              <div className="modal-title"><Icon name="jobs" size={15}/> {ts.jobDetail}</div>
              <button className="btn-icon" title={t.common.close} onClick={()=>tryClose(()=>{setSelectedJobItem(null);setJobEditForm(null);},saveJobDetail)}><Icon name="x"/></button>
            </div>
            <div className="modal-body" onChange={role!=="employee"?()=>setIsDirty(true):undefined}>
              {role!=="employee"?(
                <div className="form-grid form-grid-2">
                  <div className="form-group" style={{gridColumn:"1/-1"}}><label>Titre</label><input className="form-input" value={jobEditForm?.title||""} onChange={e=>setJobEditForm(p=>({...p,title:e.target.value}))}/></div>
                  <div className="form-group"><label>{t.customers.title}</label><div style={{padding:"8px 0",fontSize:13}}>{(()=>{const c=customers.find(x=>x.id===selectedJobItem.customerId);return c?`${c.firstName} ${c.lastName}`:"—";})()}</div></div>
                  <div className="form-group"><label>Statut</label><select className="form-select" value={jobEditForm?.status||""} onChange={e=>setJobEditForm(p=>({...p,status:e.target.value}))}>{Object.entries(t.jobs.status).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
                  <div className="form-group"><label>Date début</label><input className="form-input" type="date" value={jobEditForm?.startDate||""} onChange={e=>setJobEditForm(p=>({...p,startDate:e.target.value}))}/></div>
                  <div className="form-group"><label>Date fin</label><input className="form-input" type="date" value={jobEditForm?.endDate||""} onChange={e=>setJobEditForm(p=>({...p,endDate:e.target.value}))}/></div>
                  <div className="form-group" style={{gridColumn:"1/-1"}}>
                    <label>{t.jobs.assignedCrew}</label>
                    <select className="form-select" value={jobEditForm?.crewId||""} onChange={e=>setJobEditForm(p=>({...p,crewId:e.target.value||null}))}>
                      <option value="">— Aucune équipe —</option>
                      {(crews||[]).map(c=>(
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{gridColumn:"1/-1"}}>
                    <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                      <input type="checkbox" checked={!!jobEditForm?.fixedDate} onChange={e=>setJobEditForm(p=>({...p,fixedDate:e.target.checked}))}/>
                      {t.jobs.fixedDate}
                    </label>
                  </div>
                </div>
              ):(
                <div className="form-grid form-grid-2">
                  <div className="form-group" style={{gridColumn:"1/-1"}}><label>Titre</label><div style={{padding:"8px 0",fontSize:14,fontWeight:600}}>{jobEditForm?.title||"—"}</div></div>
                  <div className="form-group"><label>{t.customers.title}</label><div style={{padding:"8px 0",fontSize:13}}>{(()=>{const c=customers.find(x=>x.id===selectedJobItem.customerId);return c?`${c.firstName} ${c.lastName}`:"—";})()}</div></div>
                  <div className="form-group"><label>Statut</label><div style={{padding:"8px 0"}}><span className={`badge ${({pending:"badge-amber",inProgress:"badge-blue",completed:"badge-green",cancelled:"badge-gray"})[jobEditForm?.status]||"badge-gray"}`}>{t.jobs.status[jobEditForm?.status]}</span></div></div>
                  <div className="form-group"><label>Date début</label><div style={{padding:"8px 0",fontSize:13}}>{jobEditForm?.startDate||"—"}</div></div>
                  <div className="form-group"><label>Date fin</label><div style={{padding:"8px 0",fontSize:13}}>{jobEditForm?.endDate||"—"}</div></div>
                  <div className="form-group" style={{gridColumn:"1/-1"}}><label>{t.jobs.assignedCrew}</label><div style={{padding:"8px 0",fontSize:13}}>{(crews||[]).find(c=>c.id===jobEditForm?.crewId)?.name||"—"}</div></div>
                </div>
              )}
              {selectedJobItem.phases?.length>0&&(
                <div style={{marginTop:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Phases</div>
                  {selectedJobItem.phases.map((ph,i)=>(
                    <div key={ph.id} style={{fontSize:12,padding:"6px 10px",marginBottom:4,background:"var(--bg)",borderRadius:"var(--radius-sm)",borderLeft:`3px solid ${jobColor(i)}`}}>
                      <strong>{ph.title}</strong> · {ph.startDate||"?"} → {ph.endDate||"?"}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{justifyContent:"space-between"}}>
              <button className="btn btn-secondary" onClick={()=>{if(onOpenJob)onOpenJob(selectedJobItem.id);setSelectedJobItem(null);setJobEditForm(null);}}><Icon name="jobs" size={13}/> {ts.openInJobs}</button>
              {role!=="employee"&&(
                <div style={{display:"flex",gap:8}}>
                  <button className="btn btn-secondary" onClick={()=>tryClose(()=>{setSelectedJobItem(null);setJobEditForm(null);},saveJobDetail)}>{t.common.cancel}</button>
                  <button className="btn btn-primary" onClick={saveJobDetail}>{t.common.save}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {guard&&<UnsavedChangesGuard t={t} onSave={async()=>{await guard.save();setGuard(null);setIsDirty(false);}} onDiscard={()=>{guard.close();setGuard(null);setIsDirty(false);}} onKeepEditing={()=>setGuard(null)}/>}

      {/* Note form modal */}
      {noteForm!==null&&(
        <NoteForm
          t={t}
          note={noteForm.id?noteForm:null}
          customers={customers}
          crews={crews}
          jobs={jobs}
          onSave={async(form)=>{
            if(form.id) await updateScheduleNote(form.id,form);
            else await addScheduleNote(form);
            setNoteForm(null);
          }}
          onDelete={async(id)=>{ await deleteScheduleNote(id); setNoteForm(null); }}
          onClose={()=>setNoteForm(null)}
        />
      )}

      {dragGhost&&(
        <div style={{
          position:'fixed',
          left:dragGhost.x+12,
          top:dragGhost.y+12,
          background:'var(--card)',
          border:'1.5px solid var(--border)',
          borderRadius:6,
          padding:'4px 10px',
          fontSize:12,
          fontWeight:600,
          color:'var(--text)',
          pointerEvents:'none',
          zIndex:9999,
          boxShadow:'0 4px 12px rgba(0,0,0,0.15)',
          whiteSpace:'nowrap',
        }}>
          📅 {dragGhost.label}
        </div>
      )}

    </div>
  );
}

export default Schedule;
